import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import type { AppetiteParentRegisterResponseRow } from '@risksmart-app/trpc/src/types';
import type {
  Appetite_Parent_Bool_Exp,
  GetActiveRiskAppetitesQuery,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  Appetite_Status_Enum,
  Appetite_Type_Enum,
  GetActiveRiskAppetitesDocument,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import useEntityWhereFilter from '@/hooks/useEntityWhereFilter';

/**
 * Maps TRPC appetite data to match the GraphQL query structure
 */
export function mapTrpcAppetitesToGraphQL(
  trpcData: AppetiteParentRegisterResponseRow[]
): GetActiveRiskAppetitesQuery {
  return {
    appetite_parent: trpcData,
  };
}

const useAppetitesGraphqlVariables = () => {
  const whereFilter = useEntityWhereFilter<Appetite_Parent_Bool_Exp>(
    Parent_Type_Enum.Appetite,
    {
      Status: { _eq: Appetite_Status_Enum.Active },
      appetite: { AppetiteType: { _eq: Appetite_Type_Enum.Risk } },
    }
  );

  return { where: whereFilter };
};

type InternalArgs = {
  entityIds?: string[];
};

const useGetAppetitesRegisterBase = createQueryHook<
  InternalArgs,
  AppetiteParentRegisterResponseRow[],
  GetActiveRiskAppetitesQuery
>({
  trpcQueryOptions: (trpc, args) =>
    trpc.frontend.appetite.register.queryOptions({
      entityIds: args.entityIds,
    }),
  mapTrpcDataToGraphQL: mapTrpcAppetitesToGraphQL,
  graphqlDocument: GetActiveRiskAppetitesDocument,
  graphqlVariables: useAppetitesGraphqlVariables,
});

type UseGetAppetitesRegisterArgs = Record<string, never>;

/**
 * Fetches the active risk appetites register with automatic entity filtering
 * for both GraphQL and tRPC paths. Entity filtering uses useEntityFilter
 * context internally — consumers don't need to pass entityIds.
 */
export const useGetAppetitesRegister = (params: {
  queryArgs: UseGetAppetitesRegisterArgs;
  shouldSkip?: boolean;
}) => {
  const { entityIds } = useEntityFilter();

  return useGetAppetitesRegisterBase({
    queryArgs: {
      entityIds: entityIds?.length ? entityIds : undefined,
    },
    shouldSkip: params.shouldSkip,
  });
};
